import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { money, statusLabel, statusTone } from "@/lib/format";
import { ArrowUpRight, FilePlus2, Building2, TrendingUp, CircleDollarSign } from "lucide-react";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-sm text-slate-500" data-testid="dashboard-loading">Loading dashboard…</div>;

  const buckets = [
    { key: "draft", label: "Drafts" },
    { key: "info_needed", label: "Info Needed" },
    { key: "needs_verification", label: "Needs Verification" },
    { key: "awaiting_approval", label: "Awaiting Approval" },
    { key: "ready_for_submission", label: "Ready to Submit" },
    { key: "final", label: "Final" },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-6 sm:space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="kicker mb-2">Overview</div>
          <h1 className="text-3xl sm:text-4xl font-display leading-tight">Command deck</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">
            Every live transaction, verification status, and projected commission — cited and audit-ready.
          </p>
        </div>
        <Link to="/transactions/new" data-testid="new-offer-cta" className="btn-brass self-start sm:self-auto">
          <FilePlus2 size={16} /> New Offer
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Metric label="Active Transactions" value={data.total_transactions} icon={Building2} testid="metric-total" />
        <Metric label="Projected Volume" value={money(data.projected_volume)} icon={TrendingUp} testid="metric-volume" mono />
        <Metric label="Projected Commission" value={money(data.projected_commission)} icon={CircleDollarSign} testid="metric-commission" mono accent />
        <Metric label="Awaiting Approval" value={data.by_status?.awaiting_approval || 0} icon={ArrowUpRight} testid="metric-awaiting" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 grid-panel">
          <div className="border-b border-border px-5 py-3 flex items-center justify-between">
            <div className="kicker">Recent Activity</div>
            <Link to="/transactions" className="text-xs text-slate-500 hover:text-brass transition-colors">View all →</Link>
          </div>
          <div className="divide-y divide-border" data-testid="recent-list">
            {data.recent.length === 0 && (
              <div className="p-8 text-sm text-slate-500 text-center">
                No transactions yet. <Link to="/transactions/new" className="link-brass">Start your first offer</Link>.
              </div>
            )}
            {data.recent.map((t) => (
              <Link
                key={t.id}
                to={`/transactions/${t.id}`}
                data-testid={`recent-item-${t.id}`}
                className="flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors duration-150"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{t.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5 font-mono">
                    {t.property_data?.address}
                  </div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right hidden sm:block">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Offer Price</div>
                    <div className="mono-num text-sm">{money(t.offer_terms?.offer_price)}</div>
                  </div>
                  <span className={`badge-status ${statusTone[t.status]}`}>{statusLabel[t.status]}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="grid-panel">
          <div className="border-b border-border px-5 py-3">
            <div className="kicker">Pipeline</div>
          </div>
          <div className="divide-y divide-border">
            {buckets.map((b) => (
              <div key={b.key} className="px-5 py-3 flex items-center justify-between">
                <div className="text-sm text-slate-700">{b.label}</div>
                <div className="mono-num text-lg text-navy" data-testid={`bucket-${b.key}`}>
                  {data.by_status?.[b.key] || 0}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function Metric({ label, value, icon: Icon, testid, mono = false, accent = false }) {
  return (
    <div className="grid-panel p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="kicker text-[10px]">{label}</div>
        <Icon size={16} className={accent ? "text-brass" : "text-slate-400"} />
      </div>
      <div className={`mt-2 sm:mt-3 ${mono ? "font-mono" : "font-display"} ${accent ? "text-brass-700" : "text-navy"} text-2xl sm:text-3xl tracking-tight break-words`}>
        {value}
      </div>
    </div>
  );
}
